import Phaser from 'phaser';
import { PLAYER_CONFIG } from '../config';
import { InputState } from '../input/InputState';

/** Любой враг, которому можно нанести урон. */
export interface Damageable {
  x: number;
  y: number;
  active: boolean;
  isAlive(): boolean;
  takeDamage(amount: number, knockbackX: number): void;
}

type AttackDirection = 'side' | 'up' | 'down';
type AttackBox = { dx: number; dy: number; w: number; h: number };
type ArcadeBody = Phaser.Physics.Arcade.Body;

const ATTACK_ACTIVE_MS = 100;
// Круговой замах меча. Параметры масштабированы под размер героя 80×104.
const ATTACK_PIVOT_OFFSET_Y = 8;   // ниже центра спрайта — на уровне груди
const ATTACK_RADIUS = 120;         // визуальный радиус дуги
// Зона поражения — полу-диск (не кольцо), от 0 до OUTER, чтобы враги вплотную
// к герою тоже получали удар.
const ATTACK_HIT_INNER = 0;
const ATTACK_HIT_OUTER = 172;
const HURT_LOCKOUT_MS = 250;
const RESPAWN_IFRAMES_MS = 500;
const DASH_GHOST_INTERVAL_MS = 50;

export class Player extends Phaser.Physics.Arcade.Sprite {
  // Ресурсы
  public hp: number;
  public readonly maxHP: number;
  public soul: number;
  public readonly maxSoul: number;

  // Движение
  private timeSinceGrounded = Infinity;
  private jumpBufferTimer = Infinity;
  private isJumping = false;
  private wallJumpLockoutTimer = 0;
  // После wall jump игнорируем удержание клавиши «в стену» (от которой был
  // прыжок) до тех пор, пока игрок её не отпустит. Иначе после истечения
  // wallJumpLockoutTimer удержанная клавиша мгновенно возвращает героя к стене.
  // Сбрасывается при релизе клавиши или при приземлении.
  private wallJumpIgnoreDir: 'left' | 'right' | null = null;

  // Бой
  private hitbox: Phaser.GameObjects.Rectangle;
  private attackActiveTimer = 0;
  private attackCooldownTimer = 0;
  private attackDirection: AttackDirection = 'side';
  private attackOffset: AttackBox = { dx: 0, dy: 0, w: 0, h: 0 };
  private hitEnemies = new Set<Damageable>();
  // Параметры круговой hit-зоны (annular sector)
  private attackHalfPlane: 'right' | 'left' | 'up' | 'down' = 'right';
  // Чередование направления side-удара: true = сверху (CW), false = снизу (CCW)
  private nextSideFromAbove = true;
  // Overlay со СВОРДОМ — поверх героя ПОСЛЕ удара, плавно проявляется (700мс).
  // Главная текстура переключается на 'player-noweapon' instant в начале удара,
  // чтобы статический меч сразу исчез. По завершении этот overlay плавно
  // подменяет текстуру обратно на 'player' через fade-in.
  private swordRevealOverlay?: Phaser.GameObjects.Sprite;

  // Базовый scale (соответствует setDisplaySize(80, 104) при native 700×900).
  // Сохраняем чтобы корректно восстанавливать после spawn-анимации и pause/resume
  // breathing tween, который модифицирует scaleY.
  private baseScaleX!: number;
  private baseScaleY!: number;
  private breathingTween?: Phaser.Tweens.Tween;

  // Урон / неуязвимость
  private iframesTimer = 0;
  private hurtTimer = 0;
  private isDead = false;

  // Focus / лечение
  public isFocusing = false;
  private focusChargeTimer = 0;
  private focusAura?: Phaser.GameObjects.Image;

  // Dash
  public isDashing = false;
  private dashTimer = 0;
  private dashCooldownTimer = 0;
  private dashGhostTimer = 0;

  // Spell
  private isCasting = false;
  private castTimer = 0;
  private spellCooldownTimer = 0;

  // Респаун
  private respawnX: number;
  private respawnY: number;

  constructor(scene: Phaser.Scene, x: number, y: number) {
    super(scene, x, y, 'player');
    scene.add.existing(this);
    scene.physics.add.existing(this);

    this.maxHP = PLAYER_CONFIG.maxHP;
    this.hp = this.maxHP;
    this.maxSoul = PLAYER_CONFIG.maxSoul;
    this.soul = 0;
    this.respawnX = x;
    this.respawnY = y;

    // Спрайт 80×104 (на 20% меньше прежнего 100×130). Body совпадает с display.
    this.setDisplaySize(80, 104);
    this.baseScaleX = this.scaleX;
    this.baseScaleY = this.scaleY;

    const body = this.body as ArcadeBody;
    body.setCollideWorldBounds(true);
    body.setMaxVelocity(PLAYER_CONFIG.walkSpeed * 1.5, PLAYER_CONFIG.maxFallSpeed);

    // Хитбокс остаётся невидимым — нужен только для расчёта коллизий с врагами.
    // Визуально удар показывается через анимированную «слэш-арку» в startAttack.
    this.hitbox = scene.add.rectangle(0, 0, 64, 29, 0xffffff, 0);
    this.hitbox.setVisible(false);

    this.startBreathing();
  }

  // ------------------------------------------------------------------
  // SPAWN / IDLE BREATHING
  // ------------------------------------------------------------------

  private startBreathing() {
    if (this.breathingTween) this.breathingTween.stop();
    // Лёгкая пульсация scaleY (4%) каждые 700мс — idle breathing.
    this.breathingTween = this.scene.tweens.add({
      targets: this,
      scaleY: this.baseScaleY * 1.04,
      duration: 700,
      yoyo: true,
      repeat: -1,
      ease: 'Sine.inOut',
    });
  }

  /** Анимация появления из портала: alpha 0→1 + scale 0.3→1 за 600мс. */
  public playSpawnAnimation() {
    if (this.breathingTween) {
      this.breathingTween.stop();
      this.breathingTween = undefined;
    }
    this.alpha = 0;
    this.angle = 0;
    this.setScale(this.baseScaleX * 0.3, this.baseScaleY * 0.3);
    this.scene.tweens.add({
      targets: this,
      alpha: 1,
      scaleX: this.baseScaleX,
      scaleY: this.baseScaleY,
      duration: 600,
      ease: 'Quad.out',
      onComplete: () => {
        this.setScale(this.baseScaleX, this.baseScaleY);
        this.startBreathing();
      },
    });
  }

  // ------------------------------------------------------------------
  // PUBLIC API
  // ------------------------------------------------------------------

  setRespawnPoint(x: number, y: number) {
    this.respawnX = x;
    this.respawnY = y;
  }

  fullRestore() {
    this.hp = this.maxHP;
    this.soul = this.maxSoul;
  }

  setState_(hp: number, soul: number) {
    this.hp = Math.max(0, Math.min(this.maxHP, hp));
    this.soul = Math.max(0, Math.min(this.maxSoul, soul));
  }

  tick(delta: number, input: InputState) {
    // Декремент таймеров
    this.iframesTimer = Math.max(0, this.iframesTimer - delta);
    this.hurtTimer = Math.max(0, this.hurtTimer - delta);
    this.attackCooldownTimer = Math.max(0, this.attackCooldownTimer - delta);
    this.dashCooldownTimer = Math.max(0, this.dashCooldownTimer - delta);
    this.spellCooldownTimer = Math.max(0, this.spellCooldownTimer - delta);
    this.wallJumpLockoutTimer = Math.max(0, this.wallJumpLockoutTimer - delta);

    this.updateAlpha();

    if (this.isDead) return;

    // Каст заклинания — блокирует всё
    if (this.isCasting) {
      this.castTimer = Math.max(0, this.castTimer - delta);
      const body = this.body as ArcadeBody;
      body.setVelocityX(0);
      if (this.castTimer === 0) this.isCasting = false;
      this.updateAttackTimer(delta);
      return;
    }

    // Dash активен — переопределяет всё кроме каста
    if (this.isDashing) {
      this.updateDash(delta);
      this.updateAttackTimer(delta);
      return;
    }

    // Старт dash'а
    if (input.dashPressed && this.dashCooldownTimer <= 0 && this.hurtTimer <= 0) {
      this.cancelFocus();
      this.startDash();
      this.updateAttackTimer(delta);
      return;
    }

    // Старт spell'а
    if (input.spellPressed && this.canCastSpell()) {
      this.cancelFocus();
      this.startSpell();
      this.updateAttackTimer(delta);
      return;
    }

    // Focus
    this.handleFocus(delta, input);

    if (this.isFocusing) {
      const body = this.body as ArcadeBody;
      body.setVelocityX(0);
      if (input.attackPressed && this.canAttack()) {
        this.cancelFocus();
        this.startAttack(input);
      }
      this.updateAttackTimer(delta);
      return;
    }

    this.applyMovement(delta, input);
  }

  checkAttackOverlap(enemies: Damageable[]) {
    if (this.attackActiveTimer <= 0) return;

    // Annular sector check: enemy в пределах радиуса от pivot и в полупространстве
    // направления удара. Соответствует видимой дуге полукруга радиуса ATTACK_RADIUS.
    const px = this.x;
    const py = this.y + ATTACK_PIVOT_OFFSET_Y;

    for (const enemy of enemies) {
      if (!enemy.isAlive() || this.hitEnemies.has(enemy)) continue;

      const dx = enemy.x - px;
      const dy = enemy.y - py;
      const dist = Math.sqrt(dx * dx + dy * dy);

      if (dist < ATTACK_HIT_INNER || dist > ATTACK_HIT_OUTER) continue;

      let inHalfPlane = false;
      if (this.attackHalfPlane === 'right') inHalfPlane = dx > 0;
      else if (this.attackHalfPlane === 'left') inHalfPlane = dx < 0;
      else if (this.attackHalfPlane === 'up') inHalfPlane = dy < 0;
      else inHalfPlane = dy > 0;
      if (!inHalfPlane) continue;

      this.hitEnemies.add(enemy);
      const knockbackX = (enemy.x > this.x ? 1 : -1) * 240;
      enemy.takeDamage(PLAYER_CONFIG.nailDamage, knockbackX);

      if (this.attackDirection === 'down') {
        (this.body as ArcadeBody).setVelocityY(PLAYER_CONFIG.pogoBounceVelocity);
      }

      this.soul = Math.min(this.maxSoul, this.soul + PLAYER_CONFIG.soulPerHit);
      this.scene.cameras.main.shake(80, 0.005);
    }
  }

  takeContactDamage(enemy: Damageable) {
    if (this.iframesTimer > 0 || this.isDead) return;

    this.hp = Math.max(0, this.hp - PLAYER_CONFIG.enemyContactDamage);
    this.iframesTimer = PLAYER_CONFIG.invincibilityAfterHit;
    this.hurtTimer = HURT_LOCKOUT_MS;
    this.cancelFocus();
    if (this.isDashing) this.endDash();
    this.isCasting = false;
    this.castTimer = 0;

    const dir = this.x > enemy.x ? 1 : -1;
    const body = this.body as ArcadeBody;
    body.setVelocity(dir * PLAYER_CONFIG.hurtKnockbackX, PLAYER_CONFIG.hurtKnockbackY);

    this.scene.cameras.main.shake(140, 0.012);

    if (this.hp <= 0) this.die();
  }

  // ------------------------------------------------------------------
  // ДВИЖЕНИЕ + WALL SLIDE/JUMP
  // ------------------------------------------------------------------

  private applyMovement(delta: number, input: InputState) {
    const body = this.body as ArcadeBody;
    const onGround = body.blocked.down || body.touching.down;

    if (onGround) {
      this.timeSinceGrounded = 0;
      // Приземление сбрасывает «игнор клавиши после wall jump» — иначе игрок
      // не сможет идти к стене после посадки, не отпустив сначала клавишу.
      this.wallJumpIgnoreDir = null;
    } else this.timeSinceGrounded += delta;

    if (input.jumpPressed) this.jumpBufferTimer = 0;
    else this.jumpBufferTimer += delta;

    // Стена под ногами / в стороне (Wall slide / wall jump)
    const onWallLeft = !onGround && body.blocked.left;
    const onWallRight = !onGround && body.blocked.right;
    const onWall = onWallLeft || onWallRight;

    // Wall slide — тормозим скорость падения
    if (onWall && body.velocity.y > PLAYER_CONFIG.wallSlideSpeed) {
      body.setVelocityY(PLAYER_CONFIG.wallSlideSpeed);
    }

    // Сбрасываем игнор клавиши, как только игрок её отпустил
    if (this.wallJumpIgnoreDir) {
      const stillHeld =
        (this.wallJumpIgnoreDir === 'left' && input.left) ||
        (this.wallJumpIgnoreDir === 'right' && input.right);
      if (!stillHeld) this.wallJumpIgnoreDir = null;
    }

    // Горизонтальное движение блокируется во время knockback и wall jump lockout.
    // Дополнительно: после wall jump игнорируется удержание в сторону стены, от
    // которой прыгнули, пока игрок не отпустит эту клавишу.
    if (this.hurtTimer <= 0 && this.wallJumpLockoutTimer <= 0) {
      const leftActive = input.left && this.wallJumpIgnoreDir !== 'left';
      const rightActive = input.right && this.wallJumpIgnoreDir !== 'right';
      if (leftActive && !rightActive) {
        body.setVelocityX(-PLAYER_CONFIG.walkSpeed);
        this.setFlipX(true);
      } else if (rightActive && !leftActive) {
        body.setVelocityX(PLAYER_CONFIG.walkSpeed);
        this.setFlipX(false);
      } else {
        body.setVelocityX(0);
      }
    }

    // Прыжок: coyote → wall jump
    const wantsJump =
      this.hurtTimer <= 0 && this.jumpBufferTimer <= PLAYER_CONFIG.jumpBuffer;

    if (wantsJump) {
      if (this.timeSinceGrounded <= PLAYER_CONFIG.coyoteTime) {
        body.setVelocityY(PLAYER_CONFIG.jumpVelocity);
        this.timeSinceGrounded = Infinity;
        this.jumpBufferTimer = Infinity;
        this.isJumping = true;
      } else if (onWall) {
        const kickX = onWallLeft
          ? +PLAYER_CONFIG.wallJumpKick
          : -PLAYER_CONFIG.wallJumpKick;
        body.setVelocity(kickX, PLAYER_CONFIG.jumpVelocity);
        this.jumpBufferTimer = Infinity;
        this.wallJumpLockoutTimer = PLAYER_CONFIG.wallJumpLockout;
        // Игнорируем направление «в стену» пока игрок не отпустит клавишу.
        this.wallJumpIgnoreDir = onWallLeft ? 'left' : 'right';
        this.isJumping = true;
        this.setFlipX(onWallRight);
      }
    }

    // Variable jump height
    if (this.isJumping && !input.jumpHeld && body.velocity.y < 0) {
      body.setVelocityY(body.velocity.y * PLAYER_CONFIG.jumpCutMultiplier);
      this.isJumping = false;
    }
    if (body.velocity.y >= 0) this.isJumping = false;

    // Атака
    if (input.attackPressed && this.canAttack()) {
      this.startAttack(input);
    }

    this.updateAttackTimer(delta);
  }

  // ------------------------------------------------------------------
  // АТАКА
  // ------------------------------------------------------------------

  private canAttack(): boolean {
    return this.attackCooldownTimer <= 0 && this.attackActiveTimer <= 0;
  }

  private startAttack(input: InputState) {
    const body = this.body as ArcadeBody;
    const onGround = body.blocked.down || body.touching.down;

    let direction: AttackDirection;
    if (input.up) direction = 'up';
    else if (input.down && !onGround) direction = 'down';
    else direction = 'side';

    this.attackDirection = direction;

    if (direction === 'side') {
      const dx = this.flipX ? -72 : 72;
      this.attackOffset = { dx, dy: 0, w: 112, h: 40 };
    } else if (direction === 'up') {
      this.attackOffset = { dx: 0, dy: -72, w: 56, h: 104 };
    } else {
      this.attackOffset = { dx: 0, dy: 72, w: 56, h: 104 };
    }

    this.hitbox.setSize(this.attackOffset.w, this.attackOffset.h);
    this.hitbox.setPosition(this.x + this.attackOffset.dx, this.y + this.attackOffset.dy);

    this.attackActiveTimer = ATTACK_ACTIVE_MS;
    this.attackCooldownTimer = PLAYER_CONFIG.nailCooldown;
    this.hitEnemies.clear();

    // Сохраняем half-plane для проверки попаданий по дуге (annular sector)
    if (direction === 'side') this.attackHalfPlane = this.flipX ? 'left' : 'right';
    else if (direction === 'up') this.attackHalfPlane = 'up';
    else this.attackHalfPlane = 'down';

    // Чередование cw/ccw для side-удара. Up всегда «снизу», Down всегда «сверху».
    let fromAbove: boolean;
    if (direction === 'side') {
      fromAbove = this.nextSideFromAbove;
      this.nextSideFromAbove = !this.nextSideFromAbove;
    } else if (direction === 'up') {
      fromAbove = false;
    } else {
      fromAbove = true;
    }

    // Скрываем меч: меняем главную текстуру на 'player-noweapon'
    // (обе текстуры одинаковые 700×900, поэтому body не меняется)
    this.setTexture('player-noweapon');
    // Если был active sword-reveal overlay (например, новый удар во время
    // прошлой fade-in), отменяем его — меч появится снова после этого удара.
    if (this.swordRevealOverlay) {
      this.scene.tweens.killTweensOf(this.swordRevealOverlay);
      this.swordRevealOverlay.destroy();
      this.swordRevealOverlay = undefined;
    }

    // Наклон героя: «удар сверху» — вперёд, «удар снизу» — назад
    const facingLean = this.flipX ? -12 : 12;
    const lean = fromAbove ? facingLean : -facingLean;
    this.scene.tweens.add({
      targets: this,
      angle: lean,
      duration: 70,
      yoyo: true,
      ease: 'Quad.out',
      onComplete: () => {
        this.angle = 0;
      },
    });

    this.spawnSlashEffect(direction, fromAbove);
  }

  private spawnSlashEffect(direction: AttackDirection, fromAbove: boolean) {
    // Круговой замах меча. Направление зависит от направления удара и facing'а
    // героя. Для side: чередование cw/ccw через fromAbove.
    const facingRight = !this.flipX;
    const pivotX = this.x;
    const pivotY = this.y + ATTACK_PIVOT_OFFSET_Y;
    const radius = ATTACK_RADIUS;
    const sweepAmount = Math.PI;

    let arcStart: number;
    let sweepDir: 1 | -1;

    if (direction === 'side') {
      if (fromAbove) {
        // «Удар сверху» — от верха через сторону взгляда вниз
        arcStart = -Math.PI / 2;
        sweepDir = facingRight ? 1 : -1;
      } else {
        // «Удар снизу» — от низа через сторону взгляда вверх
        arcStart = Math.PI / 2;
        sweepDir = facingRight ? -1 : 1;
      }
    } else if (direction === 'up') {
      // Всегда «снизу» — от нижне-переднего сектора через верх
      arcStart = facingRight ? Math.PI / 4 : (Math.PI * 3) / 4;
      sweepDir = facingRight ? -1 : 1;
    } else {
      // 'down' — всегда «сверху», от верхне-переднего через низ
      arcStart = facingRight ? -Math.PI / 4 : (-Math.PI * 3) / 4;
      sweepDir = facingRight ? 1 : -1;
    }

    const slash = this.scene.add.graphics();
    slash.setDepth(15);

    // Меч-спрайт летит впереди дуги-следа. Origin (1, 0.5) — кончик на центре,
    // рукоять тянется назад. Поворот по касательной к дуге, чтобы меч смотрел
    // вдоль направления движения.
    // Меч: программная текстура 150×36 (рукоять слева x=0..32, клинок 32..148).
    // Origin (0, 0.5) — anchor у рукояти. Позиция фиксирована в pivot, кончик
    // всегда тянется наружу по setRotation(currentAngle). Это исключает
    // путаницу с инверсией ориентации при разных углах.
    const sword = this.scene.add.image(pivotX, pivotY, 'sword');
    sword.setDisplaySize(ATTACK_RADIUS, 36);
    sword.setOrigin(0, 0.5);
    sword.setDepth(16);
    // Стартовая поза должна совпадать с t=0 в tween, иначе один-два render-кадра
    // меч успевает показаться в дефолтном rotation=0 (горизонтально вправо) до
    // первого onUpdate.
    sword.setRotation(arcStart);

    const trailWidth = Math.PI * 0.45;

    this.scene.tweens.addCounter({
      from: 0,
      to: 1,
      duration: 130,
      ease: 'Quad.out',
      onUpdate: (tween) => {
        const t = tween.getValue() ?? 0;
        const currentAngle = arcStart + sweepDir * sweepAmount * t;
        const trailStartAngle = currentAngle - sweepDir * trailWidth;
        const fade = 1 - t;

        // === Дуга-смазанный след ===
        slash.clear();
        slash.lineStyle(14, 0xfff3a8, fade * 0.55);
        slash.beginPath();
        slash.arc(pivotX, pivotY, radius, trailStartAngle, currentAngle, sweepDir < 0);
        slash.strokePath();
        slash.lineStyle(5, 0xffffff, fade * 0.95);
        slash.beginPath();
        slash.arc(pivotX, pivotY, radius, trailStartAngle, currentAngle, sweepDir < 0);
        slash.strokePath();

        // === Меч ===
        // Anchor (рукоять) фиксирован в pivot, поворот = currentAngle.
        // Кончик клинка автоматически оказывается на дуге в направлении θ.
        sword.setPosition(pivotX, pivotY);
        sword.setRotation(currentAngle);
        sword.setAlpha(t < 0.8 ? 1 : (1 - t) / 0.2);
      },
      onComplete: () => {
        slash.destroy();
        sword.destroy();
      },
    });
  }

  private updateAttackTimer(delta: number) {
    if (this.attackActiveTimer > 0) {
      this.attackActiveTimer = Math.max(0, this.attackActiveTimer - delta);
      this.hitbox.setPosition(this.x + this.attackOffset.dx, this.y + this.attackOffset.dy);
      if (this.attackActiveTimer === 0) {
        this.startSwordReveal();
      }
    }
    this.syncSwordRevealOverlay();
  }

  // ------------------------------------------------------------------
  // SWORD REVEAL — плавное возвращение меча через 700мс после удара
  // ------------------------------------------------------------------

  private startSwordReveal() {
    if (this.swordRevealOverlay) {
      this.scene.tweens.killTweensOf(this.swordRevealOverlay);
      this.swordRevealOverlay.destroy();
    }
    // Спрайт с мечом ('player' — это hero.png) появляется alpha=0, fade в 1.
    // Когда полностью непрозрачен, переключаем главную текстуру и удаляем overlay.
    const overlay = this.scene.add.sprite(this.x, this.y, 'player');
    overlay.setDisplaySize(80, 104);
    overlay.setDepth(11);
    overlay.setAlpha(0);
    this.swordRevealOverlay = overlay;
    this.syncSwordRevealOverlay();

    this.scene.tweens.add({
      targets: overlay,
      alpha: 1,
      duration: 700,
      onComplete: () => {
        if (this.active) {
          this.setTexture('player');
          this.setDisplaySize(80, 104);
        }
        overlay.destroy();
        if (this.swordRevealOverlay === overlay) {
          this.swordRevealOverlay = undefined;
        }
      },
    });
  }

  private syncSwordRevealOverlay() {
    const o = this.swordRevealOverlay;
    if (!o) return;
    o.setPosition(this.x, this.y);
    o.setFlipX(this.flipX);
    o.setAngle(this.angle);
  }

  // ------------------------------------------------------------------
  // SPELL (Vengeful Spirit)
  // ------------------------------------------------------------------

  private canCastSpell(): boolean {
    return (
      this.spellCooldownTimer <= 0 &&
      this.soul >= PLAYER_CONFIG.spellCost &&
      this.hurtTimer <= 0 &&
      !this.isDashing
    );
  }

  private startSpell() {
    this.isCasting = true;
    this.castTimer = PLAYER_CONFIG.spellCastLockout;
    this.spellCooldownTimer = PLAYER_CONFIG.spellCooldown;
    this.soul -= PLAYER_CONFIG.spellCost;

    const dir: 1 | -1 = this.flipX ? -1 : 1;
    const px = this.x + dir * 36;
    const py = this.y - 6;

    // Сцена слушает это событие и спавнит снаряд
    this.scene.events.emit('player-cast', { x: px, y: py, dir });

    // Лёгкая отдача и flash
    const body = this.body as ArcadeBody;
    body.setVelocityX(-dir * 80);
    this.scene.cameras.main.flash(120, 255, 230, 150);
  }

  // ------------------------------------------------------------------
  // FOCUS / HEAL
  // ------------------------------------------------------------------

  private handleFocus(delta: number, input: InputState) {
    const body = this.body as ArcadeBody;
    const onGround = body.blocked.down || body.touching.down;
    const canFocus =
      this.hp < this.maxHP &&
      this.soul >= PLAYER_CONFIG.focusCost &&
      onGround &&
      this.iframesTimer <= 0 &&
      this.hurtTimer <= 0;

    if (input.focusHeld && canFocus) {
      if (!this.isFocusing) {
        this.isFocusing = true;
        this.focusChargeTimer = 0;
        // Image с canvas-радиальным градиентом — даёт по-настоящему плавный
        // тёплый туман без ступеней alpha. Blend ADD усиливает «магическое»
        // свечение поверх героя.
        this.focusAura = this.scene.add.image(this.x, this.y, 'focus-aura');
        this.focusAura.setBlendMode(Phaser.BlendModes.ADD);
        this.focusAura.setDepth(8);
      }
      this.focusChargeTimer += delta;

      if (this.focusAura) {
        this.focusAura.setPosition(this.x, this.y);
        const progress = Math.min(1, this.focusChargeTimer / PLAYER_CONFIG.focusDuration);
        // Скейл растёт от 0.55 до 1.05 → диаметр тумана 140..270 px (текстура 256).
        // Лёгкий пульс ±4% по синусу — «дыхание» ауры.
        const baseScale = 0.55 + progress * 0.5;
        const pulse = 1 + 0.04 * Math.sin(this.focusChargeTimer / 220);
        this.focusAura.setScale(baseScale * pulse);
        // Альфа растёт по мере зарядки
        this.focusAura.setAlpha(0.45 + progress * 0.45);
      }

      if (this.focusChargeTimer >= PLAYER_CONFIG.focusDuration) {
        this.heal();
        this.focusChargeTimer = 0;
      }
    } else if (this.isFocusing) {
      this.cancelFocus();
    }
  }

  private cancelFocus() {
    this.isFocusing = false;
    this.focusChargeTimer = 0;
    if (this.focusAura) {
      this.focusAura.destroy();
      this.focusAura = undefined;
    }
  }

  private heal() {
    this.hp = Math.min(this.maxHP, this.hp + 1);
    this.soul = Math.max(0, this.soul - PLAYER_CONFIG.focusCost);
    this.scene.cameras.main.flash(180, 255, 230, 150);
  }

  // ------------------------------------------------------------------
  // DASH
  // ------------------------------------------------------------------

  private startDash() {
    this.isDashing = true;
    this.dashTimer = PLAYER_CONFIG.dashDuration;
    this.dashCooldownTimer = PLAYER_CONFIG.dashCooldown;
    this.iframesTimer = Math.max(this.iframesTimer, PLAYER_CONFIG.dashDuration);

    const dir = this.flipX ? -1 : 1;
    const body = this.body as ArcadeBody;
    // Снимаем cap maxVelocityX на время dash, иначе body.setVelocity капается
    // на walkSpeed*1.5 и dashSpeed не достигается.
    body.setMaxVelocity(PLAYER_CONFIG.dashSpeed, PLAYER_CONFIG.maxFallSpeed);
    body.setVelocity(dir * PLAYER_CONFIG.dashSpeed, 0);
    body.setAllowGravity(false);

    this.dashGhostTimer = 0;
    this.spawnDashGhost();
  }

  private endDash() {
    this.isDashing = false;
    const body = this.body as ArcadeBody;
    body.setMaxVelocity(PLAYER_CONFIG.walkSpeed * 1.5, PLAYER_CONFIG.maxFallSpeed);
    body.setAllowGravity(true);
  }

  private updateDash(delta: number) {
    this.dashTimer -= delta;
    this.dashGhostTimer -= delta;
    if (this.dashGhostTimer <= 0) {
      this.spawnDashGhost();
      this.dashGhostTimer = DASH_GHOST_INTERVAL_MS;
    }
    if (this.dashTimer <= 0) {
      this.endDash();
    }
  }

  private spawnDashGhost() {
    // Ghost ×1.5 от размера героя, alpha 0.25 (вдвое прозрачнее прежнего)
    const ghost = this.scene.add.sprite(this.x, this.y, 'player');
    ghost.setScale(this.scaleX * 1.5, this.scaleY * 1.5);
    ghost.setTintFill(0xffaa66);
    ghost.setAlpha(0.25);
    ghost.setFlipX(this.flipX);
    this.scene.tweens.add({
      targets: ghost,
      alpha: 0,
      duration: 220,
      onComplete: () => ghost.destroy(),
    });
  }

  // ------------------------------------------------------------------
  // СМЕРТЬ / РЕСПАУН
  // ------------------------------------------------------------------

  private die() {
    this.isDead = true;
    this.cancelFocus();
    if (this.isDashing) this.endDash();
    this.isCasting = false;
    this.castTimer = 0;

    const body = this.body as ArcadeBody;
    body.setVelocity(0, 0);

    this.scene.cameras.main.fade(400, 0, 0, 0);
    this.scene.cameras.main.once('camerafadeoutcomplete', () => this.respawn());
  }

  private respawn() {
    this.setPosition(this.respawnX, this.respawnY);
    const body = this.body as ArcadeBody;
    body.setVelocity(0, 0);
    body.setAllowGravity(true);
    this.hp = this.maxHP;
    this.iframesTimer = RESPAWN_IFRAMES_MS;
    this.hurtTimer = 0;
    this.isDead = false;
    if (this.swordRevealOverlay) {
      this.scene.tweens.killTweensOf(this.swordRevealOverlay);
      this.swordRevealOverlay.destroy();
      this.swordRevealOverlay = undefined;
    }
    this.setTexture('player');
    this.setDisplaySize(80, 104);
    this.baseScaleX = this.scaleX;
    this.baseScaleY = this.scaleY;
    this.scene.events.emit('player-respawn');
    this.playSpawnAnimation();
    this.scene.cameras.main.fadeIn(300, 0, 0, 0);
  }

  // ------------------------------------------------------------------
  // ВИЗУАЛЬНОЕ
  // ------------------------------------------------------------------

  private updateAlpha() {
    if (this.iframesTimer > 0) {
      this.alpha = Math.floor(this.iframesTimer / 80) % 2 === 0 ? 0.3 : 1;
    } else {
      this.alpha = 1;
    }
  }
}
